import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { useAuth } from '@/contexts/AuthContext';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import LoginModal from '@/components/LoginModal';
import StreakHighlightRow from '@/components/StreakHighlightRow';
import StreakAtRiskRow from '@/components/StreakAtRiskRow';
import TierCutsRow from '@/components/TierCutsRow';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Star, Check, Zap, Clock, Flame, ArrowUp, ArrowDown, X, Search } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_CONFIG = {
  king: {
    icon: '\u{1F451}',
    title: 'Kings Challenge',
    // Round P108ar — dynamic count derived from the live challenge
    // endpoint response (see `totalCount` state). Prevents subtitle
    // drift when a ticker joins/leaves the curated list.
    subtitle: (n) => `${n} Dividend King${n === 1 ? '' : 's'}`,
    description: 'Dividend Kings have increased their dividend every year for **50+ consecutive years**. These are the most elite dividend-paying companies in America.',
    endpoint: 'kings-challenge',
    minYears: 50,
    nearKingThreshold: null,
    barColors: { high: 'bg-yellow-500', mid: 'bg-amber-500', low: 'bg-amber-400' },
    progressGradient: 'from-yellow-500 to-amber-400',
    collectedBg: 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800/40 hover:border-yellow-300',
    collectedCircle: 'bg-yellow-500',
  },
  aristocrat: {
    icon: '\u{1F3C6}',
    title: 'Aristocrats Challenge',
    subtitle: (n) => `${n} Dividend Aristocrat${n === 1 ? '' : 's'}`,
    description: 'Dividend Aristocrats are S&P 500 members that have increased dividends for **25+ consecutive years**. The gold standard of reliable dividend payers.',
    endpoint: 'aristocrats-challenge',
    minYears: 25,
    nearKingThreshold: 45,
    barColors: { high: 'bg-blue-500', mid: 'bg-blue-400', low: 'bg-blue-300' },
    progressGradient: 'from-blue-500 to-indigo-400',
    collectedBg: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/40 hover:border-blue-300',
    collectedCircle: 'bg-blue-500',
  },
  achiever: {
    icon: '\u{1F396}\uFE0F',
    title: 'Achievers Challenge',
    subtitle: (n) => `${n} Dividend Achiever${n === 1 ? '' : 's'}`,
    description: 'Dividend Achievers have increased dividends for **10+ consecutive years**. Proven track records of shareholder commitment.',
    endpoint: 'achievers-challenge',
    minYears: 10,
    nearKingThreshold: null,
    barColors: { high: 'bg-green-500', mid: 'bg-green-400', low: 'bg-emerald-400' },
    progressGradient: 'from-green-500 to-emerald-400',
    collectedBg: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800/40 hover:border-green-300',
    collectedCircle: 'bg-green-500',
  },
};

const TIER_TABS = [
  { key: 'king', path: '/kings-challenge', label: 'Kings', icon: '\u{1F451}' },
  { key: 'aristocrat', path: '/aristocrats-challenge', label: 'Aristocrats', icon: '\u{1F3C6}' },
  { key: 'achiever', path: '/achievers-challenge', label: 'Achievers', icon: '\u{1F396}\uFE0F' },
];

function StreakBar({ years, maxYears, tier }) {
  const pct = Math.min((years / maxYears) * 100, 100);
  const cfg = TIER_CONFIG[tier]?.barColors || TIER_CONFIG.king.barColors;
  const color = years >= 60 ? cfg.high : years >= 40 ? cfg.mid : cfg.low;
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-bold tabular-nums w-10 text-right">{years} yr</span>
    </div>
  );
}

export default function TierChallenge({ tier = 'king' }) {
  const { isDark } = useMarketTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sort state for the new sort bar above the list. Default 'years'
  // desc matches the historical "longest streak first" ordering so
  // the user-visible default is identical to before this feature.
  const [sortBy, setSortBy] = useState('years');
  const [sortDir, setSortDir] = useState('desc');
  // Live substring filter — narrows the visible list by ticker OR
  // company name. Especially useful on the Achievers page (200+).
  const [filterText, setFilterText] = useState('');
  const [collected, setCollected] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Round 95bu (Jun 2026) — inline Compare (Aristocrats & Achievers).
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [prefetchedNews, setPrefetchedNews] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);

  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.king;

  const fetchWatchlist = async () => {
    if (!user) { setWatchlist([]); return; }
    try {
      const { data } = await axios.get(`${API}/portfolio`, { withCredentials: true });
      setWatchlist(data.map(p => p.ticker));
    } catch { setWatchlist([]); }
  };

  useEffect(() => { fetchWatchlist(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginModalOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) {
        await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
        setWatchlist(w => w.filter(t => t !== ticker));
      } else {
        await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
        setWatchlist(w => [...w, ticker]);
      }
      // Let AppHeader refresh the badge/count instantly
      window.dispatchEvent(new Event('watchlist-changed'));
    } catch (err) {
      console.error('Failed to toggle watchlist:', err);
    }
  };

  // Streak-extension overlay: pull the Streak Highlight row's data
  // (K/A/A who raised in last 60 days) so we can flame-tag matching
  // tiles AND show a "X / Y raised this quarter" stat in the
  // progress card. Pure DB read — same endpoint as the dashboard row.
  const [recentlyRaised, setRecentlyRaised] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/screener/streaks-this-week`, { params: { days: 90 } });
        if (cancelled) return;
        const map = {};
        (res.data?.items || []).forEach((it) => {
          if ((it.dividend_tier || '').toLowerCase() !== tier) return;
          map[it.ticker] = it;
        });
        setRecentlyRaised(map);
      } catch {
        if (!cancelled) setRecentlyRaised({});
      }
    })();
    return () => { cancelled = true; };
  }, [tier]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/stocks/${cfg.endpoint}`, { withCredentials: true })
      .then(res => {
        setStocks(res.data.stocks || []);
        setCollected(res.data.collected || 0);
        setTotalCount(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cfg.endpoint]);

  // Fetch the daily-tier-refresh cron's last-run timestamp so we can show
  // users when prices were last auto-refreshed. Runs once per tab visit.
  useEffect(() => {
    axios.get(`${API}/stocks/tier-refresh-status`)
      .then(res => setRefreshStatus(res.data))
      .catch(() => setRefreshStatus(null));
  }, []);

  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then(res => setPrefetchedNews(res.data)).catch(() => {});
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Round 79 — StockModal back-restore
  useReopenModalOnMount(handleStockClick);

  const maxYears = stocks.length > 0 ? stocks[0]?.dividend_streak_years || 72 : 72;
  // Feb 2026 — Collection count is now PORTFOLIO-ONLY. Watchlist entries
  // are free to add so they don't reflect real commitment; the challenge
  // is meaningful only when you've actually spent money to own the ticker.
  const liveCollected = stocks.filter(s => s.in_portfolio).length;
  const collectedPct = totalCount > 0 ? Math.round((liveCollected / totalCount) * 100) : 0;
  // Round P108ar (Feb 2026) — "Nearest to Kings" must EXCLUDE tickers
  // already at 50+ years (they're Kings themselves, not "nearest to").
  // Cap at streak <= 49 so the card only surfaces genuine near-misses
  // (45-49 yrs for aristocrats). Kings-tier callers keep the
  // no-upper-bound behaviour by leaving nearKingThreshold=null.
  const nearKingStocks = cfg.nearKingThreshold
    ? stocks.filter(s => {
        const y = s.dividend_streak_years || 0;
        return y >= cfg.nearKingThreshold && y < 50;
      })
    : [];
  // Quarterly-raise stat for the progress card. Counts how many of
  // THIS tier's universe raised within the last 90 days, plus how
  // many of those the user holds. Pure intersection — no extra fetch.
  const raisedRecentlyCount = stocks.filter(s => recentlyRaised[s.ticker]).length;
  const raisedRecentlyOwned = stocks.filter(s => recentlyRaised[s.ticker] && s.in_portfolio).length;

  // Apply the chosen sort to the displayed stocks. Pure client-side
  // — backend already returns the universe sorted by years desc, but
  // we reapply that as the default to keep behavior consistent if
  // the backend ever changes ordering. Memoized so re-renders from
  // unrelated state changes don't re-sort.
  const sortedStocks = useMemo(() => {
    // Step 1: apply substring filter against ticker OR name (case-insensitive).
    const q = filterText.trim().toLowerCase();
    const filtered = q
      ? stocks.filter((s) =>
          (s.ticker || '').toLowerCase().includes(q) ||
          (s.name || '').toLowerCase().includes(q),
        )
      : stocks;

    // Step 2: sort the filtered list.
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = {
      name:   (a, b) => (a.name || a.ticker || '').localeCompare(b.name || b.ticker || ''),
      ticker: (a, b) => (a.ticker || '').localeCompare(b.ticker || ''),
      years:  (a, b) => (a.dividend_streak_years || 0) - (b.dividend_streak_years || 0),
      price:  (a, b) => (a.price || 0) - (b.price || 0),
      yield:  (a, b) => (a.dividend_yield || 0) - (b.dividend_yield || 0),
    }[sortBy] || ((a, b) => (a.dividend_streak_years || 0) - (b.dividend_streak_years || 0));
    arr.sort((a, b) => cmp(a, b) * dir);
    return arr;
  }, [stocks, sortBy, sortDir, filterText]);

  // Click handler for sort-bar chips. Same field clicked again toggles
  // direction; switching to a different field picks a sensible default
  // direction (desc for numerics, asc for alphabetic).
  const onSortClick = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir(['name', 'ticker'].includes(field) ? 'asc' : 'desc');
    }
  };
  const isDefaultSort = sortBy === 'years' && sortDir === 'desc';
  const clearSort = () => { setSortBy('years'); setSortDir('desc'); };

  // Format the daily-refresh timestamp for the bottom-of-card badge.
  // Shows "Today 9:52 AM ET" if same day, "Yesterday 9:52 AM ET", or "Apr 18, 9:52 AM ET".
  const formatRefreshLabel = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      // Render in America/New_York so the label matches when the cron runs
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
      const parts = fmt.formatToParts(d);
      const get = (t) => parts.find(p => p.type === t)?.value || '';
      const dayLabel = `${get('month')} ${get('day')}`;
      const timeLabel = `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()} ET`;
      // Compare dates in America/New_York
      const nowNY = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const dNY = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const sameDay = nowNY.toDateString() === dNY.toDateString();
      const diffDays = Math.floor((nowNY - dNY) / (1000 * 60 * 60 * 24));
      if (sameDay) return `Today ${timeLabel}`;
      if (diffDays === 1) return `Yesterday ${timeLabel}`;
      return `${dayLabel}, ${timeLabel}`;
    } catch { return null; }
  };
  const refreshLabel = formatRefreshLabel(refreshStatus?.last_refresh_at);

  return (
    <div className="min-h-screen min-h-[100svh] bg-background transition-colors duration-500" data-testid={`${tier}-challenge-page`}>

      {/* Challenge Sub-header */}
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-2 border-b border-border/30 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BackOrHomeButton testid={`${tier}-challenge-back-or-home-btn`} showEscHint={false} />
          <h2 className="text-base font-bold tracking-tight flex items-center gap-2" data-testid="challenge-title">
            <span className="text-lg">{cfg.icon}</span>
            {cfg.title}
          </h2>
        </div>
        <span className="text-xs text-muted-foreground">{totalCount} stocks</span>
        {/* Mobile: the Undervaluation Heatmap pill wraps to its own
            line, centered. Desktop (≥sm): sits at the far right. Uses
            `basis-full sm:basis-auto` on a wrapper so the flex line
            break is deterministic on narrow viewports and doesn't
            depend on unreliable natural-wrap heuristics. */}
        <div className="basis-full sm:basis-auto flex justify-center sm:justify-end">
          <Link to={`/${tier === 'king' ? 'kings' : tier === 'aristocrat' ? 'aristocrats' : 'achievers'}-heatmap`}
            className="px-2.5 py-1 rounded-full text-[11px] font-bold text-white hover:opacity-90 transition-opacity"
            style={{ background: 'linear-gradient(to right, rgb(165,0,38), rgb(0,104,55))' }}
            data-testid={`challenge-heatmap-link-${tier}`}>
            🔥 Undervaluation Heatmap →
          </Link>
        </div>
      </div>

      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tier Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b" data-testid="challenge-tabs">
          {TIER_TABS.map(tab => {
            const isActive = location.pathname === tab.path;
            return (
              <Link key={tab.key} to={tab.path} data-testid={`tab-${tab.key}`}>
                <button className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  isActive
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}>
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              </Link>
            );
          })}
        </div>

        {/* Progress Card */}
        <div className="rounded-xl border bg-card p-4 mb-5" data-testid="challenge-progress-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Your Collection</h2>
              <p className="text-3xl font-bold mt-1">
                {liveCollected} <span className="text-lg text-muted-foreground font-normal">/ {totalCount}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-primary">{collectedPct}%</div>
              <p className="text-[10px] text-muted-foreground mt-0.5 font-semibold uppercase tracking-wide">Collection Progress</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {liveCollected === 0 ? 'Buy any of these to start your collection' :
                 liveCollected === totalCount ? 'You collected them all!' :
                 `${totalCount - liveCollected} more to go`}
              </p>
            </div>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${cfg.progressGradient} transition-all duration-1000`}
              style={{ width: `${collectedPct}%` }}
            />
          </div>
          {/* Compact status row — combines the "X/Y raised in last 90 days"
              stat (when present), the optional "Your portfolio caught N" pill,
              AND the Last refreshed badge into a single line so the card
              stays tight. Top of card to the Your Collection header = 16px
              (p-4); raised/refreshed row to bottom of card = 16px (p-4) —
              equal padding both sides. */}
          {(raisedRecentlyCount > 0 || refreshLabel) && (
            <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px]">
              {raisedRecentlyCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300"
                  data-testid="tier-recently-raised"
                  title={`${raisedRecentlyCount} ${cfg.title.replace(' Challenge','')}s have raised their dividend in the last 90 days. ${raisedRecentlyOwned > 0 ? `Your portfolio caught ${raisedRecentlyOwned} of them.` : ''}`}
                >
                  <Flame className="w-3 h-3 text-orange-500 shrink-0" />
                  <span className="font-semibold">
                    {raisedRecentlyCount} / {totalCount} raised in the last 90 days
                  </span>
                </span>
              )}
              {raisedRecentlyOwned > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold ring-1 ring-emerald-500/30">
                  Your portfolio caught {raisedRecentlyOwned}
                </span>
              )}
              {refreshLabel && (
                <span
                  className="inline-flex items-center gap-1 text-muted-foreground ml-auto"
                  data-testid="tier-refresh-badge"
                  title="Prices auto-refresh weekdays at 9:50 AM ET, 20 min after market open."
                >
                  <Clock className="w-3 h-3 shrink-0" />
                  <span className="whitespace-nowrap">Last refreshed: {refreshLabel}</span>
                </span>
              )}
            </div>
          )}
          {!user && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" />
              Log in to track which ones you own or are watching
            </p>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4" dangerouslySetInnerHTML={{
          __html: cfg.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        }} />

        {/* Near King Callout (Aristocrats only) */}
        {tier === 'aristocrat' && nearKingStocks.length > 0 && (
          <div className="rounded-lg border border-yellow-200 dark:border-yellow-800/40 bg-yellow-50/50 dark:bg-yellow-950/10 p-4 mb-5" data-testid="near-king-callout">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">Nearest to King Status</span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              These Aristocrats have 45–49 years of consecutive increases — just a few years from becoming Dividend Kings:
            </p>
            <div className="flex flex-wrap gap-2">
              {nearKingStocks.map(s => (
                <button
                  key={s.ticker}
                  onClick={() => handleStockClick(s.ticker)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors cursor-pointer"
                  data-testid={`near-king-${s.ticker}`}
                >
                  {'\u{1F451}'} {s.ticker}
                  <span className="font-normal text-yellow-600 dark:text-yellow-400">{s.dividend_streak_years}yr</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Streak surfacing rows — same components used on the
            Dashboard, scoped via `tierFilter` so each Challenge page
            only shows recent action for its own tier. Both components
            null-render when there are no items, so quiet pages stay
            quiet (no empty boxes). */}
        <StreakHighlightRow
          onStockClick={handleStockClick}
          ownedTickers={stocks.filter(s => s.in_portfolio).map(s => s.ticker)}
          tierFilter={tier}
        />
        <StreakAtRiskRow
          onStockClick={handleStockClick}
          ownedTickers={stocks.filter(s => s.in_portfolio).map(s => s.ticker)}
          tierFilter={tier}
        />
        {/* Round 95f — "Cut from List": former members of THIS tier
            removed this quarter. Null-renders when empty. */}
        <TierCutsRow
          onStockClick={handleStockClick}
          ownedTickers={stocks.filter(s => s.in_portfolio).map(s => s.ticker)}
          tierFilter={tier}
        />

        {/* Stock List */}
        {loading ? (
          <div className="space-y-3" data-testid="challenge-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg shimmer" />
            ))}
          </div>
        ) : (
          <>
            {/* Sort bar — five field chips with up/down direction arrows
                plus a Clear button that only appears when the user has
                deviated from the default (years desc). Click the active
                field to toggle direction; click any other field to make
                it active with a sensible default direction. The search
                input on the right is a pure client-side substring
                filter against ticker OR name, with a "X of Y" count
                next to it so users see exactly how much narrowed. */}
            <div
              className="flex items-center gap-1.5 mb-2 flex-wrap text-[11px]"
              data-testid="challenge-sort-bar"
            >
              <span className="text-muted-foreground font-semibold uppercase tracking-wider mr-1">
                Sort
              </span>
              {[
                { key: 'name',   label: 'Name' },
                { key: 'ticker', label: 'Ticker' },
                { key: 'years',  label: 'Years' },
                { key: 'price',  label: 'Price' },
                { key: 'yield',  label: 'Yield' },
              ].map(({ key, label }) => {
                const active = sortBy === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onSortClick(key)}
                    data-testid={`challenge-sort-${key}`}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                    {active && (sortDir === 'asc'
                      ? <ArrowUp className="w-3 h-3" />
                      : <ArrowDown className="w-3 h-3" />)}
                  </button>
                );
              })}
              {!isDefaultSort && (
                <button
                  type="button"
                  onClick={clearSort}
                  data-testid="challenge-sort-clear"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold bg-muted/40 hover:bg-rose-500/15 text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                  title="Reset to default sort: longest streak first"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}

              {/* Right-side: search input + filter count */}
              <div className="ml-auto flex items-center gap-1.5">
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
                  <input
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filter ticker or name…"
                    data-testid="challenge-filter-input"
                    className="pl-7 pr-7 py-1 rounded-md border border-border/60 bg-card text-[11px] w-44 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-colors"
                  />
                  {filterText && (
                    <button
                      type="button"
                      onClick={() => setFilterText('')}
                      data-testid="challenge-filter-clear"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-rose-500 p-0.5 rounded transition-colors"
                      title="Clear filter"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <span
                  className="text-muted-foreground tabular-nums whitespace-nowrap"
                  data-testid="challenge-filter-count"
                  title={filterText ? `${sortedStocks.length} match${sortedStocks.length === 1 ? '' : 'es'} of ${stocks.length} total` : `${stocks.length} stocks`}
                >
                  {filterText
                    ? `${sortedStocks.length} of ${stocks.length}`
                    : `${stocks.length}`}
                </span>
              </div>
            </div>

            <div className="space-y-1.5" data-testid="challenge-list">
            {sortedStocks.map((stock, idx) => {
              const isNearKing = tier === 'aristocrat' && cfg.nearKingThreshold && (stock.dividend_streak_years || 0) >= cfg.nearKingThreshold;
              const isWatching = watchlist.includes(stock.ticker);
              const isOwned = !!stock.in_portfolio;
              return (
                <div
                  key={stock.ticker}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all group border ${
                    isOwned
                      ? cfg.collectedBg
                      : 'border-transparent hover:bg-muted/50'
                  }`}
                  onClick={() => handleStockClick(stock.ticker)}
                  data-testid={`challenge-row-${stock.ticker}`}
                >
                  <span className="text-xs font-bold text-muted-foreground/60 w-6 text-right tabular-nums">
                    {idx + 1}
                  </span>

                  {/* Watchlist star — identical UX to rest of site */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWatchlist(stock.ticker);
                    }}
                    title={isWatching ? 'Remove from watchlist' : 'Add to watchlist'}
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      isWatching
                        ? 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                        : 'bg-muted text-muted-foreground/30 hover:bg-muted/80 hover:text-muted-foreground/60'
                    }`}
                    data-testid={`challenge-watch-${stock.ticker}`}
                  >
                    <Star className={`w-3.5 h-3.5 ${isWatching ? 'fill-current' : ''}`} />
                  </button>

                  {/* Portfolio ownership indicator — visual only, green check when owned */}
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      isOwned
                        ? cfg.collectedCircle + ' text-white'
                        : 'bg-transparent border border-muted-foreground/10'
                    }`}
                    title={isOwned ? 'You own this in your portfolio' : 'Not in your portfolio'}
                    data-testid={`challenge-owned-${stock.ticker}`}
                  >
                    {isOwned && <Check className="w-3 h-3" strokeWidth={3} />}
                  </div>

                  {/* Feb 2026 — Two-row layout so the FULL company
                      name gets to breathe (was truncated to 19
                      chars), and the streak bar drops onto the
                      same line as the ticker. Line 1: full name.
                      Line 2: ticker + streak bar. */}
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-semibold text-sm group-hover:text-primary transition-colors leading-tight break-words"
                      data-testid={`challenge-row-${stock.ticker}-name`}
                    >
                      {stock.name || stock.ticker}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-mono text-xs text-yellow-600 dark:text-yellow-400 font-bold shrink-0">{stock.ticker}</span>
                      {isNearKing && <span className="text-[10px] shrink-0" title="Near King status">{'\u{1F451}'}</span>}
                      {recentlyRaised[stock.ticker] && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1 py-px rounded text-[8px] font-bold uppercase bg-amber-100 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 ring-1 ring-amber-400/60 shrink-0"
                          title={`Just raised dividend +${Number(recentlyRaised[stock.ticker].recent_div_change_pct || 0).toFixed(1)}% on ${recentlyRaised[stock.ticker].recent_div_last_date}`}
                          data-testid={`challenge-streak-flame-${stock.ticker}`}
                        >
                          <Flame className="w-2 h-2 text-orange-500" />
                          +{Number(recentlyRaised[stock.ticker].recent_div_change_pct || 0).toFixed(1)}%
                        </span>
                      )}
                      <StreakBar years={stock.dividend_streak_years || cfg.minYears} maxYears={maxYears} tier={tier} />
                    </div>
                  </div>

                  <div className="text-right shrink-0 hidden sm:block">
                    <span className="text-sm font-mono block">${stock.price?.toFixed(2) || '--'}</span>
                    <span className="text-xs font-mono text-green-600 dark:text-green-400">
                      <span className="text-muted-foreground font-sans mr-1">Yield</span>
                      {stock.dividend_yield?.toFixed(2) || '0'}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); setPrefetchedNews(null); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        prefetchedNews={prefetchedNews}
        user={user}
        onLoginRequired={() => setLoginModalOpen(true)}
        onPositionsChanged={() => {
          window.dispatchEvent(new Event('portfolio-changed'));
        }}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        initialTab="dividend-info"
        initialChartPeriod={null}
        onCompareClick={(t) => {
          if (t) { setCompareTicker(t); setCompareOpen(true); }
        }}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />

      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />

      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Back to Top — sits between the page content and the disclaimer
          so a long Achievers list (200+ rows) doesn't force the user
          to scroll all the way back manually. Pure browser scroll, no
          layout shift while invisible. */}
      <div className="container mx-auto max-w-5xl px-4 pb-0 flex justify-center">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="challenge-back-to-top"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline underline-offset-2 transition-colors"
          title="Scroll back to top of the page"
        >
          <ArrowUp className="w-3 h-3" />
          Back to top
        </button>
      </div>

      <SiteDisclaimer />
    </div>
  );
}
